import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { assignmentAPI, courseAPI, categoryAPI } from "@/lib/api";
import { WorkspaceLayout } from "@/components/layout/WorkspaceLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { 
  ArrowLeft, 
  Save, 
  Loader2, 
  FileCheck, 
  BookOpen, 
  Layers,
  Calendar as CalendarIcon,
  Settings
} from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { cn } from "@/lib/utils";
import RichTextEditor from "@/components/editor/RichTextEditor";

export default function AssignmentEditorPage() {
  const { workspaceId, assignmentId } = useParams();
  const navigate = useNavigate();
  const isNew = !assignmentId || assignmentId === "new";
  
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [courses, setCourses] = useState([]);
  const [categoriesList, setCategoriesList] = useState([]);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    instructions: "",
    start_date: new Date(),
    due_date: (() => { const d = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000); d.setHours(23, 59, 59, 999); return d; })(),
    max_score: 100,
    category_id: null,
    is_visible: true,
    submission_open: true,
    course_id: null,
    section_id: null,
  });

  useEffect(() => {
    loadCourses();
    categoryAPI.list(workspaceId).then(res => setCategoriesList(res.data)).catch(() => {});
    if (!isNew && assignmentId) {
      setLoading(true);
      loadAssignment();
    }
  }, [assignmentId, isNew]);

  const loadCourses = async () => {
    try {
      const response = await courseAPI.list(workspaceId);
      setCourses(response.data);
    } catch (error) {
      console.error("Failed to load courses:", error);
    }
  };

  const loadAssignment = async () => {
    try {
      const response = await assignmentAPI.get(workspaceId, assignmentId);
      const data = response.data;
      setFormData({
        title: data.title || "",
        description: data.description || "",
        instructions: data.instructions || "",
        start_date: new Date(data.start_date),
        due_date: new Date(data.due_date),
        max_score: data.max_score || 100,
        category_id: data.category_id || null,
        is_visible: data.is_visible !== false,
        submission_open: data.submission_open !== false,
        course_id: data.course_id || null,
        section_id: data.section_id || null,
      });
    } catch (error) {
      console.error("Failed to load assignment:", error);
      toast.error("Erreur lors du chargement du devoir");
      navigate(`/workspace/${workspaceId}/assignments`);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!formData.title.trim()) {
      toast.error("Le titre est requis");
      return;
    }

    setSaving(true);
    try {
      const data = {
        ...formData,
        start_date: formData.start_date.toISOString(),
        due_date: formData.due_date.toISOString(),
      };

      if (isNew) {
        await assignmentAPI.create(workspaceId, data);
        toast.success("Devoir créé avec succès");
      } else {
        await assignmentAPI.update(workspaceId, assignmentId, data);
        toast.success("Devoir mis à jour");
      }
      navigate(`/workspace/${workspaceId}/assignments`);
    } catch (error) {
      console.error("Failed to save assignment:", error);
      toast.error("Erreur lors de la sauvegarde");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <WorkspaceLayout>
        <div className="page-container flex items-center justify-center py-16">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </WorkspaceLayout>
    );
  }

  return (
    <WorkspaceLayout>
      <div className="page-container animate-fade-in">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(`/workspace/${workspaceId}/assignments`)}
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex-1">
            <h1 className="text-2xl font-semibold tracking-tight">
              {isNew ? "Nouveau Devoir" : "Modifier le Devoir"}
            </h1>
            <p className="text-muted-foreground">
              {isNew ? "Créez un nouveau devoir pour vos apprenants" : "Modifiez les détails du devoir"}
            </p>
          </div>
          <Button onClick={handleSave} disabled={saving} className="gap-2" data-testid="save-assignment-btn">
            {saving ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Save className="h-4 w-4" />
            )}
            {saving ? "Sauvegarde..." : "Sauvegarder"}
          </Button>
        </div>

        <div className="grid gap-6 lg:grid-cols-3">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Title & Description */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileCheck className="h-5 w-5 text-blue-600" />
                  Informations générales
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="title">Titre *</Label>
                  <Input
                    id="title"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="Ex: TP Docker - Conteneurisation d'une application"
                    data-testid="assignment-title-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="description">Description courte</Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Brève description du devoir..."
                    rows={2}
                    data-testid="assignment-description-input"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Instructions Editor */}
            <Card>
              <CardHeader>
                <CardTitle>Instructions détaillées</CardTitle>
                <p className="text-sm text-muted-foreground">
                  Utilisez l'éditeur pour créer des instructions riches avec images, vidéos et mise en forme
                </p>
              </CardHeader>
              <CardContent>
                <RichTextEditor
                  content={formData.instructions}
                  onChange={(content) => setFormData({ ...formData, instructions: content })}
                  placeholder="Rédigez les instructions détaillées du devoir ici..."
                />
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Course Attachment */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BookOpen className="h-5 w-5" />
                  Rattachement
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Rattacher à un cours</Label>
                  <Select
                    value={formData.course_id || "none"}
                    onValueChange={(value) => setFormData({ 
                      ...formData, 
                      course_id: value === "none" ? null : value,
                      section_id: value === "none" ? null : formData.section_id
                    })}
                  >
                    <SelectTrigger data-testid="assignment-course-select">
                      <SelectValue placeholder="Aucun cours" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Aucun cours</SelectItem>
                      {courses.map((course) => (
                        <SelectItem key={course.id} value={course.id}>
                          {course.title}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {formData.course_id && (
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <Layers className="h-4 w-4" />
                      Rattacher à une section
                    </Label>
                    <Select
                      value={formData.section_id || "none"}
                      onValueChange={(value) => setFormData({ 
                        ...formData, 
                        section_id: value === "none" ? null : value 
                      })}
                    >
                      <SelectTrigger data-testid="assignment-section-select">
                        <SelectValue placeholder="Aucune section" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Directement au cours</SelectItem>
                        {courses
                          .find(c => c.id === formData.course_id)
                          ?.sections?.map((section, index) => (
                            <SelectItem key={section.id} value={section.id}>
                              Section {index + 1}: {section.title}
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Dates */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CalendarIcon className="h-5 w-5" />
                  Dates
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Date de début</Label>
                  <div className="flex gap-2">
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className={cn("flex-1 justify-start text-left font-normal")}
                          data-testid="assignment-start-date"
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {format(formData.start_date, "PPP 'à' HH:mm", { locale: fr })}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0">
                        <Calendar
                          mode="single"
                          selected={formData.start_date}
                          onSelect={(date) => {
                            if (date) {
                              date.setHours(formData.start_date.getHours(), formData.start_date.getMinutes(), 0, 0);
                              setFormData({ ...formData, start_date: new Date(date) });
                            }
                          }}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                    <Input
                      type="time"
                      className="w-[110px]"
                      value={`${String(formData.start_date.getHours()).padStart(2,'0')}:${String(formData.start_date.getMinutes()).padStart(2,'0')}`}
                      onChange={(e) => {
                        const [h, m] = e.target.value.split(':').map(Number);
                        const d = new Date(formData.start_date);
                        d.setHours(h, m, 0, 0);
                        setFormData({ ...formData, start_date: d });
                      }}
                      data-testid="assignment-start-time"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Date limite</Label>
                  <div className="flex gap-2">
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className={cn("flex-1 justify-start text-left font-normal")}
                          data-testid="assignment-due-date"
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {format(formData.due_date, "PPP 'à' HH:mm", { locale: fr })}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0">
                        <Calendar
                          mode="single"
                          selected={formData.due_date}
                          onSelect={(date) => {
                            if (date) {
                              date.setHours(formData.due_date.getHours(), formData.due_date.getMinutes(), 59, 999);
                              setFormData({ ...formData, due_date: new Date(date) });
                            }
                          }}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                    <Input
                      type="time"
                      className="w-[110px]"
                      value={`${String(formData.due_date.getHours()).padStart(2,'0')}:${String(formData.due_date.getMinutes()).padStart(2,'0')}`}
                      onChange={(e) => {
                        const [h, m] = e.target.value.split(':').map(Number);
                        const d = new Date(formData.due_date);
                        d.setHours(h, m, 59, 999);
                        setFormData({ ...formData, due_date: d });
                      }}
                      data-testid="assignment-due-time"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Settings */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Settings className="h-5 w-5" />
                  Paramètres
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="max_score">Note maximale</Label>
                  <Input
                    id="max_score"
                    type="number"
                    min="1"
                    value={formData.max_score}
                    onChange={(e) => setFormData({ ...formData, max_score: parseInt(e.target.value) || 100 })}
                    data-testid="assignment-max-score-input"
                  />
                </div>
                {categoriesList.length > 0 && (
                  <div className="space-y-2">
                    <Label htmlFor="assignment-category">Catégorie</Label>
                    <select
                      id="assignment-category"
                      value={formData.category_id || ""}
                      onChange={(e) => setFormData({ ...formData, category_id: e.target.value || null })}
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                      data-testid="assignment-category-select"
                    >
                      <option value="">Sans catégorie</option>
                      {categoriesList.map(cat => (
                        <option key={cat.id} value={cat.id}>{cat.name}</option>
                      ))}
                    </select>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Visibility */}
            <Card>
              <CardHeader>
                <CardTitle>Visibilité</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label>Visible pour les candidats</Label>
                    <p className="text-sm text-muted-foreground">
                      {formData.is_visible 
                        ? "Les candidats peuvent voir ce devoir" 
                        : "Ce devoir est caché aux candidats"}
                    </p>
                  </div>
                  <Switch
                    checked={formData.is_visible}
                    onCheckedChange={(checked) => setFormData({ ...formData, is_visible: checked })}
                    data-testid="assignment-visibility-toggle"
                  />
                </div>
                <div className="border-t my-4" />
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label>Soumission autorisée</Label>
                    <p className="text-sm text-muted-foreground">
                      {formData.submission_open 
                        ? "Les candidats peuvent soumettre leur travail" 
                        : "La soumission est bloquée (fermeture manuelle)"}
                    </p>
                  </div>
                  <Switch
                    checked={formData.submission_open}
                    onCheckedChange={(checked) => setFormData({ ...formData, submission_open: checked })}
                    data-testid="assignment-submission-toggle"
                  />
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </WorkspaceLayout>
  );
}
